'use client';

import { useQuery } from '@tanstack/react-query';
import { getMainCatsClient } from '@util/firestore/categories';
import { useAuth } from 'context/AuthContext';
import { prefix } from '@util/imagePrefix';
import { CategoryDocument } from 'models/category';
import Image from 'next/image';
import { useCreateListing } from '../_context';

// The `categories_new/personal-watercraft` doc is a banner-only entry: it can
// be missing an image and an index, and may not exist in every environment,
// so the PWC card gets these fallbacks to stay selectable like other sports.
const PWC_IMAGE = `${prefix}/assets/ride_types/pwc_1920x2560.png`;
const PWC_INDEX = 6;
const PWC_FALLBACK = {
  id: 'categories_new/personal-watercraft',
  name: 'Personal Watercraft',
  product_cats: { ride_type: 'PWC' },
  ride_types: ['PWC'],
} as CategoryDocument;

export default function SelectListingSport() {
  const { form, goToStep, product, categoryInfo, setCategoryInfo } =
    useCreateListing();
  const { userDoc } = useAuth();

  const { data: sports } = useQuery({
    queryKey: ['sports'],
    queryFn: () => getMainCatsClient(true),
  });

  const otherCats = ['Casual', 'Collectibles', 'Giveaways'];

  const pwcDoc = sports?.find((c) => c.product_cats.ride_type === 'PWC');
  const pwcCard: CategoryDocument = {
    ...(pwcDoc ?? PWC_FALLBACK),
    image: pwcDoc?.image || PWC_IMAGE,
    index: pwcDoc?.index ?? PWC_INDEX,
  };
  const sportCards = sports
    ? [
        ...sports.filter(
          (c) =>
            !otherCats.includes(c.name) && c.product_cats.ride_type !== 'PWC'
        ),
        pwcCard,
      ].sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
    : [];

  const errors = form.formState.errors.ride_types?.message;
  return (
    <div className="flex w-full flex-col justify-between px-4 py-16 sm:mx-auto sm:max-w-[100rem] sm:px-0 sm:py-32">
      <div className="flex flex-col gap-y-4">
        <h1 className="text-[2.5rem] font-semibold text-black sm:text-[3rem]">
          Which sport is this item for?
        </h1>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:gap-8">
          {sportCards.map((c, _) => {
            const supportText =
              c.product_cats.ride_type === 'Street'
                ? 'Cruiser, Street, Adventure'
                : c.product_cats.ride_type === 'Cycling'
                ? 'Road Bikes, E-Bikes, Mountain Bikes'
                : c.product_cats.ride_type === 'PWC'
                ? 'Sea-Doo, WaveRunner, Jet Ski'
                : c.app_desc;

            return (
              <button
                type="button"
                className={`flex h-[30rem] w-full cursor-pointer flex-col justify-between overflow-hidden rounded-2xl pb-4 sm:w-[23rem] sm:pb-8 ${
                  c.product_cats.ride_type === product.ride_types?.[0]
                    ? 'border border-brand-secondary bg-inherit'
                    : 'bg-[#F1F1F1]'
                }`}
                key={_}
                onClick={() => {
                  setCategoryInfo([c]);
                  goToStep('select-listing-category');
                }}
              >
                <div className="mt-16 flex w-full justify-center sm:mt-0 sm:justify-normal">
                  {c.image && (
                    <Image
                      src={c.image ?? ''}
                      alt={c.product_cats.ride_type ?? 'Ride Type Image'}
                      width={200}
                      height={200}
                      className="-ml-10 h-44 w-full object-contain sm:-ml-20 sm:mt-12 sm:h-64 sm:object-cover sm:object-right"
                      priority
                    />
                  )}
                </div>

                <div className="mx-4 flex flex-col items-start gap-y-1 sm:mx-8 sm:gap-y-2">
                  <h3 className="text-[1.8rem] font-semibold text-zinc-800">
                    {c.product_cats.ride_type}
                  </h3>

                  {supportText && (
                    <span className="text-start text-[1.5rem] text-zinc-500">
                      {supportText}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <h3 className="mt-8 text-[1.8rem] font-semibold text-black sm:text-[2rem]">
          Other categories
        </h3>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:flex sm:flex-row sm:gap-8">
          {sports
            ?.filter(
              (c) =>
                otherCats.includes(c.product_cats.category || '') &&
                (c.product_cats.category !== 'Giveaways' ||
                  userDoc?.roles?.experiences)
            )
            .map((c) => {
              const image =
                c.product_cats.category === 'Casual'
                  ? `https://cdn.mxlocker.com/assets/support-casual_1920x2560.png`
                  : c.product_cats.category === 'Collectibles'
                  ? `https://cdn.mxlocker.com/assets/support-collectibles_1920x2560.png`
                  : c.image;
              return (
                <button
                  type="button"
                  className={`flex h-[35rem] w-full cursor-pointer flex-col justify-between overflow-hidden rounded-2xl pb-4 sm:w-[23rem] sm:pb-8 ${
                    c.id === categoryInfo[0]?.id
                      ? 'border border-brand-secondary bg-inherit'
                      : 'bg-[#F1F1F1] outline-none'
                  }`}
                  key={c.product_cats.category}
                  onClick={() => {
                    setCategoryInfo([c]);
                    if (c.product_cats.category === 'Casual') {
                      goToStep('select-listing-category');
                    } else {
                      form.setValue(
                        'ride_types',
                        c.product_cats.ride_type
                          ? [c.product_cats.ride_type]
                          : []
                      );
                      form.setValue('category', c.product_cats.category!);
                      form.setValue('category1', null);
                      form.setValue('category2', null);
                      form.setValue('category3', null);
                      form.setValue('categories', [c.product_cats.category!]);
                      goToStep('create-listing');
                    }
                    window.scrollTo({ top: 0 });
                  }}
                >
                  {image && (
                    <Image
                      src={image}
                      alt={c.product_cats.category || 'Category Image'}
                      width={500}
                      height={500}
                      className="-ml-16 mt-2 h-[90rem] w-[90rem] object-cover sm:-ml-32 sm:mt-4 sm:h-[120rem] sm:w-[120rem]"
                      priority
                    />
                  )}

                  <div className="mx-4 flex flex-col items-start gap-y-1 sm:mx-8 sm:gap-y-2">
                    <h3 className="text-[1.8rem] font-semibold text-zinc-800">
                      {c.product_cats.category}
                    </h3>
                  </div>
                </button>
              );
            })}
        </div>
      </div>

      <div className="mt-16 flex w-full flex-col items-center gap-[1rem]">
        {errors && <p className="text-[1.5rem] text-brand-red">{errors}</p>}
        <button
          className="flex w-[25rem] grow items-center justify-center gap-x-2 rounded-[1.5rem] bg-brand-lightest-gray px-6 py-3 text-[1.6rem] text-black hover:bg-brand-light-gray sm:grow-0 sm:px-8 sm:py-4 sm:text-[1.8rem]"
          type="button"
          onClick={() => {
            goToStep('search');
          }}
        >
          Back
        </button>
      </div>
    </div>
  );
}
